import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  createWorkspace,
  deleteWorkspace,
  listWorkspacesAll,
  updateWorkspace,
} from "./api/boardApi";
import { errorToast, successToast } from "../../redux/actionCreator";
import { getSession } from "../../utils/session";
import "./workspacesAdmin.scss";
import "./dashboards.scss";

// Admin-only page for managing workspaces. Lets admin/super_admin create,
// rename, recolor, and (when empty) delete workspaces.
export default function WorkspacesAdmin() {
  const navigate = useNavigate();
  const role = getSession()?.role;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ name: "", description: "", color: "#ef3a3a", icon: "" });

  const load = async () => {
    setLoading(true);
    try {
      const res = await listWorkspacesAll();
      setList(res.data || []);
    } catch (e) { errorToast(e.message); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (role !== "admin" && role !== "super_admin") {
    return (
      <div className="ws-admin denied">
        <h2>Workspaces</h2>
        <p>This page is restricted to admins.</p>
      </div>
    );
  }

  const startEdit = (w) => {
    setEditing(w);
    setDraft({ name: w.name, description: w.description || "", color: w.color || "#ef3a3a", icon: w.icon || "" });
    setShowCreate(true);
  };

  const startCreate = () => {
    setEditing(null);
    setDraft({ name: "", description: "", color: "#ef3a3a", icon: "" });
    setShowCreate(true);
  };

  const save = async () => {
    if (!draft.name.trim()) return;
    try {
      if (editing) {
        const res = await updateWorkspace(editing._id, draft);
        setList((arr) => arr.map((x) => (x._id === editing._id ? res.data : x)));
        successToast("Workspace updated");
      } else {
        const res = await createWorkspace(draft);
        setList((arr) => [...arr, res.data]);
        successToast("Workspace created");
      }
      setShowCreate(false);
    } catch (e) { errorToast(e.message); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete workspace "${w.name}"?`)) return;
    try {
      await deleteWorkspace(w._id);
      setList((arr) => arr.filter((x) => x._id !== w._id));
      successToast("Workspace deleted");
    } catch (e) { errorToast(e.message); }
  };

  return (
    <div className="ws-admin">
      <header className="ws-admin-head">
        <h2>Workspaces</h2>
        <div>
          <Button variant="outline-secondary" onClick={() => navigate("/boards")} className="me-2">← Boards</Button>
          <Button variant="primary" onClick={startCreate}>+ New workspace</Button>
        </div>
      </header>

      {loading ? (
        <div className="muted"><Spinner size="sm" animation="border" /> Loading…</div>
      ) : (
        <div className="ws-grid">
          {list.map((w) => (
            <div key={w._id} className={`ws-card ${w.archived ? "archived" : ""}`}>
              <div className="ws-card-head">
                <span className="ws-dot" style={{ background: w.color || "#ef3a3a" }}>{w.icon || "▥"}</span>
                <h4>{w.name}</h4>
                {w.default && <span className="ws-default-chip">default</span>}
              </div>
              {w.description && <p className="ws-card-desc">{w.description}</p>}
              <div className="ws-card-actions">
                <button className="linkish" onClick={() => startEdit(w)}>Edit</button>
                {!w.default && (
                  <button className="linkish del" onClick={() => remove(w)}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit workspace" : "New workspace"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control autoFocus value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={2} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} />
          </Form.Group>
          <div className="row">
            <Form.Group className="mb-3 col-6">
              <Form.Label>Color</Form.Label>
              <Form.Control type="color" value={draft.color} onChange={(e) => setDraft((d) => ({ ...d, color: e.target.value }))} />
            </Form.Group>
            <Form.Group className="mb-3 col-6">
              <Form.Label>Icon (1–2 chars)</Form.Label>
              <Form.Control maxLength={2} value={draft.icon} onChange={(e) => setDraft((d) => ({ ...d, icon: e.target.value }))} placeholder="e.g. ▥" />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={!draft.name.trim()}>{editing ? "Save" : "Create"}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
